import { forwardRef, ReactElement, useImperativeHandle, useRef, useState } from 'react';
import Popper from '@mui/material/Popper';
import type { Instance, Placement } from '@popperjs/core';
import { tbColors } from 'styles/colors';

export interface ChartTooltipPopperFrameHandle {
  openAt: (positionOrElement: number | HTMLElement, y?: number) => void;
  close: () => void;
}

interface ChartTooltipPopperFrameProps {
  children: ReactElement;
  placement: Placement;
  // The default is applied in the forwardRef render function.
  // eslint-disable-next-line react/require-default-props
  withDropShadow?: boolean;
}

const ARROW_SIZE = 18.75;
const ARROW_BASE = ARROW_SIZE * 2;
const ARROW_OFFSET = ARROW_SIZE;

export const ChartTooltipPopperFrame = forwardRef<ChartTooltipPopperFrameHandle, ChartTooltipPopperFrameProps>(
  function ChartTooltipPopperFrame({ children, placement, withDropShadow = true }, ref) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null as HTMLDivElement | null);
    const popperRef = useRef(null as Instance | null);
    const tooltipPositionRef = useRef({ x: 0, y: 0 });
    const anchorElementRef = useRef<HTMLElement | null>(null);

    useImperativeHandle(ref, () => ({
      openAt: (positionOrElement: number | HTMLElement, y = 0) => {
        if (positionOrElement instanceof HTMLElement) {
          anchorElementRef.current = positionOrElement;
        } else {
          anchorElementRef.current = null;
          tooltipPositionRef.current = { x: positionOrElement, y };
        }

        if (popperRef.current != null) {
          popperRef.current.update();
        }

        setOpen(true);
      },
      close: () => setOpen(false),
    }));

    return (
      <div ref={rootRef}>
        <Popper
          popperRef={popperRef}
          open={open}
          placement={placement}
          keepMounted
          anchorEl={{
            getBoundingClientRect: () => {
              if (anchorElementRef.current != null) {
                return anchorElementRef.current.getBoundingClientRect();
              }

              const screenPosition = rootRef.current?.getBoundingClientRect();

              return new DOMRect(
                tooltipPositionRef.current.x + (screenPosition?.left ?? 0),
                tooltipPositionRef.current.y + (screenPosition?.top ?? 0),
                0,
                0
              );
            },
          }}
          modifiers={[{ name: 'offset', options: { offset: [0, ARROW_OFFSET] } }]}
          sx={{
            pointerEvents: 'none',
            '& > .chart-tooltip-popper-content': {
              borderRadius: 1,
              maxWidth: 'unset',
              position: 'relative',
            },
            ...(withDropShadow
              ? {
                  '& > .chart-tooltip-popper-content > *': {
                    boxShadow: '0 4px 120px rgba(26, 26, 26, 0.16)',
                  },
                }
              : {}),
            '& .chart-tooltip-arrow': {
              backgroundColor: tbColors.all.snow[50],
              ...(withDropShadow ? { filter: 'drop-shadow(0 4px 20px rgba(26, 26, 26, 0.28))' } : {}),
              height: ARROW_BASE,
              position: 'absolute',
              width: ARROW_SIZE,
              zIndex: 2,
            },
            // The 2:1 base-to-length ratio makes the chart tooltip arrow's tip 90deg.
            '&[data-popper-placement^="right"] .chart-tooltip-arrow': {
              clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
              left: -ARROW_OFFSET,
              top: '50%',
              transform: 'translateY(-50%)',
            },
            '&[data-popper-placement^="left"] .chart-tooltip-arrow': {
              clipPath: 'polygon(100% 50%, 0 0, 0 100%)',
              right: -ARROW_OFFSET,
              top: '50%',
              transform: 'translateY(-50%)',
            },
            '&[data-popper-placement^="bottom"] .chart-tooltip-arrow': {
              clipPath: 'polygon(50% 0, 0 100%, 100% 100%)',
              height: ARROW_SIZE,
              left: '50%',
              top: -ARROW_OFFSET,
              transform: 'translateX(-50%)',
              width: ARROW_BASE,
            },
            '&[data-popper-placement^="top"] .chart-tooltip-arrow': {
              clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
              bottom: -ARROW_OFFSET,
              height: ARROW_SIZE,
              left: '50%',
              width: ARROW_BASE,
              transform: 'translateX(-50%)',
            },
            '& > .chart-tooltip-popper-content, .MuiPaper-root': {
              backgroundColor: 'transparent',
              overflow: 'visible',
              boxShadow: 'none',
            },
          }}
        >
          <div className="chart-tooltip-popper-content">
            <div className="chart-tooltip-arrow" aria-hidden="true" />
            {children}
          </div>
        </Popper>
      </div>
    );
  }
);
